import { type FC, useMemo, useState } from 'react';
import Overlay from './overlay';
import { SvgIcon } from './svg-icon';
import styles from '../styles/date-range-form-field.module.scss';

type DateRange = {
    fromDate: string;
    toDate: string;
};

type CellState = {
    inRange: boolean;
    isRangeStart: boolean;
    isRangeEnd: boolean;
    isRangeRowStart: boolean;
    isRangeRowEnd: boolean;
    isSelectedBoundary: boolean;
};

const YEAR_OPTIONS_DELTA = 10;

const WEEK_LABELS = ['日', '月', '火', '水', '木', '金', '土'] as const;

const parseIsoDate = (value: string) => {
    if (!value) {
        return null;
    }
    const [year, month, day] = value.split('-').map((part) => Number(part));
    if (!year || !month || !day) {
        return null;
    }
    return new Date(year, month - 1, day);
};

const toIsoDate = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toDisplayDate = (value: string) => {
    const date = parseIsoDate(value);
    if (!date) {
        return '';
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}/${month}/${day}`;
};

const DateRangeFormField: FC<{
    label: string;
    placeholder?: string;
    dateRange: DateRange;
    onChange: (newRange: DateRange) => void;
}> = ({ label, placeholder = '日付範囲を選択してください', dateRange, onChange }) => {
    const { fromDate, toDate } = dateRange;
    const [draftRange, setDraftRange] = useState<DateRange>({ fromDate, toDate });

    const hasDraftRange = Boolean(draftRange.fromDate && draftRange.toDate);
    const hasRangeSpan = hasDraftRange && draftRange.fromDate !== draftRange.toDate;

    const [isOpen, setIsOpen] = useState(false);
    const [showYearDropdown, setShowYearDropdown] = useState(false);
    const [anchorDate, setAnchorDate] = useState<string | null>(null);
    const [visibleMonth, setVisibleMonth] = useState(() => {
        const initial = parseIsoDate(fromDate) ?? new Date();
        return new Date(initial.getFullYear(), initial.getMonth(), 1);
    });

    const dateTitle = `${visibleMonth.getFullYear()}年${visibleMonth.getMonth() + 1}月`;
    const selectedYear = visibleMonth.getFullYear();

    const yearOptions = useMemo(() => {
        const startYear = selectedYear - YEAR_OPTIONS_DELTA;
        return Array.from({ length: YEAR_OPTIONS_DELTA * 2 + 1 }, (_, idx) => startYear + idx);
    }, [selectedYear]);

    const leadingBlanks = useMemo(() => {
        return Array.from({ length: visibleMonth.getDay() }, (_, idx) => `blank-${idx}`);
    }, [visibleMonth]);

    const monthDays = useMemo(() => {
        const totalDays = new Date(
            visibleMonth.getFullYear(),
            visibleMonth.getMonth() + 1,
            0
        ).getDate();

        return Array.from({ length: totalDays }, (_, idx) => {
            const date = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), idx + 1);
            return {
                dateStr: toIsoDate(date),
                dayNumber: idx + 1,
            };
        });
    }, [visibleMonth]);

    const closePicker = () => {
        setShowYearDropdown(false);
        setAnchorDate(null);
        setIsOpen(false);
    };

    const applyDraftAndClose = () => {
        onChange({ ...draftRange });
        closePicker();
    };

    const clearRange = () => {
        setDraftRange({ fromDate: '', toDate: '' });
        setAnchorDate(null);
    };

    const handleDayClick = (selectedDate: string) => {
        if (!draftRange.fromDate || !draftRange.toDate || !anchorDate) {
            setDraftRange({ fromDate: selectedDate, toDate: selectedDate });
            setAnchorDate(selectedDate);
            return;
        }

        if (selectedDate === anchorDate) {
            setDraftRange({ fromDate: selectedDate, toDate: selectedDate });
            return;
        }

        const newFrom = selectedDate < anchorDate ? selectedDate : anchorDate;
        const newTo = selectedDate < anchorDate ? anchorDate : selectedDate;
        setDraftRange({ fromDate: newFrom, toDate: newTo });
        setAnchorDate(null);
    };

    const displayValue =
        fromDate && toDate ? `${toDisplayDate(fromDate)} 〜 ${toDisplayDate(toDate)}` : '';

    const isHighlightedDate = (dateStr: string | undefined) => {
        return Boolean(
            dateStr &&
            hasRangeSpan &&
            dateStr >= draftRange.fromDate &&
            dateStr <= draftRange.toDate
        );
    };

    const getCellState = (dateStr: string, idx: number): CellState => {
        const isStart = hasDraftRange && dateStr === draftRange.fromDate;
        const isEnd = hasDraftRange && dateStr === draftRange.toDate;
        const isSelectedBoundary = isStart || isEnd;
        const inRange =
            hasDraftRange && dateStr > draftRange.fromDate && dateStr < draftRange.toDate;
        const isRangeStart = hasRangeSpan && isStart;
        const isRangeEnd = hasRangeSpan && isEnd;

        const isRangeCell = inRange || isRangeStart || isRangeEnd;
        const columnIndex = (leadingBlanks.length + idx) % 7;
        const isPrevHighlighted = isHighlightedDate(monthDays[idx - 1]?.dateStr);
        const isNextHighlighted = isHighlightedDate(monthDays[idx + 1]?.dateStr);
        const isRangeRowStart = isRangeCell && (columnIndex === 0 || !isPrevHighlighted);
        const isRangeRowEnd = isRangeCell && (columnIndex === 6 || !isNextHighlighted);

        return {
            inRange,
            isRangeStart,
            isRangeEnd,
            isRangeRowStart,
            isRangeRowEnd,
            isSelectedBoundary,
        };
    };

    const makeCellStyle = (cellState: CellState) => {
        return [
            styles.dayCell,
            cellState.inRange ? styles.inRange : '',
            cellState.isRangeStart ? styles.rangeStart : '',
            cellState.isRangeEnd ? styles.rangeEnd : '',
            cellState.isRangeRowStart ? styles.rangeRowStart : '',
            cellState.isRangeRowEnd ? styles.rangeRowEnd : '',
            cellState.isSelectedBoundary ? styles.selectedBoundary : '',
        ]
            .filter(Boolean)
            .join(' ');
    };

    return (
        <div className={styles.container}>
            <div className={styles.field}>
                <div className={styles.label}>{label}</div>
                <button
                    type="button"
                    className={styles.trigger}
                    onClick={() => {
                        setDraftRange({ fromDate, toDate });
                        const selected = parseIsoDate(fromDate) ?? new Date();
                        setVisibleMonth(new Date(selected.getFullYear(), selected.getMonth(), 1));
                        setAnchorDate(null);
                        setShowYearDropdown(false);
                        setIsOpen(true);
                    }}
                >
                    <span className={displayValue ? styles.value : styles.placeholder}>
                        {displayValue || placeholder}
                    </span>
                    <SvgIcon name="calendar" className={styles.triggerIcon} />
                </button>
            </div>
            {isOpen && (
                <Overlay>
                    <div
                        className={styles.calendarContainer}
                        onClick={() => {
                            setShowYearDropdown(false);
                        }}
                    >
                        <button type="button" className={styles.closeButton} onClick={closePicker}>
                            <SvgIcon name="closeCircle" />
                        </button>
                        <div className={styles.calendarTitle}>{placeholder}</div>
                        <div className={styles.calendarCard}>
                            <div className={styles.headerRow}>
                                <div className={styles.dateTitleGroup}>
                                    <button
                                        type="button"
                                        className={styles.yearDropdownButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowYearDropdown((prev) => !prev);
                                        }}
                                    >
                                        <span className={styles.dateTitle}>{dateTitle}</span>
                                        <SvgIcon
                                            className={styles.dateChevron}
                                            name="rightChevron"
                                        />
                                    </button>
                                    {showYearDropdown && (
                                        <div
                                            className={styles.yearDropdown}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            {yearOptions.map((year) => (
                                                <button
                                                    key={year}
                                                    type="button"
                                                    className={styles.yearOption}
                                                    onClick={() => {
                                                        setVisibleMonth(
                                                            new Date(
                                                                year,
                                                                visibleMonth.getMonth(),
                                                                1
                                                            )
                                                        );
                                                        setShowYearDropdown(false);
                                                    }}
                                                >
                                                    {year}年
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.navButtons}>
                                    <button
                                        type="button"
                                        className={styles.navButton}
                                        onClick={() => {
                                            setShowYearDropdown(false);
                                            setVisibleMonth(
                                                new Date(
                                                    visibleMonth.getFullYear(),
                                                    visibleMonth.getMonth() - 1,
                                                    1
                                                )
                                            );
                                        }}
                                    >
                                        <SvgIcon name="leftChevron" />
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.navButton}
                                        onClick={() => {
                                            setShowYearDropdown(false);
                                            setVisibleMonth(
                                                new Date(
                                                    visibleMonth.getFullYear(),
                                                    visibleMonth.getMonth() + 1,
                                                    1
                                                )
                                            );
                                        }}
                                    >
                                        <SvgIcon name="rightChevron" />
                                    </button>
                                </div>
                            </div>
                            <div className={styles.weekLabels}>
                                {WEEK_LABELS.map((labelText) => (
                                    <div key={labelText} className={styles.weekLabel}>
                                        {labelText}
                                    </div>
                                ))}
                            </div>
                            <div className={styles.daysGrid}>
                                {leadingBlanks.map((blank) => (
                                    <div key={blank} className={styles.blankCell} />
                                ))}
                                {monthDays.map(({ dateStr, dayNumber }, idx) => {
                                    return (
                                        <button
                                            key={dateStr}
                                            type="button"
                                            className={makeCellStyle(getCellState(dateStr, idx))}
                                            onClick={() => handleDayClick(dateStr)}
                                        >
                                            <span className={styles.dayNumber}>{dayNumber}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className={styles.actionsRow}>
                            <button
                                type="button"
                                className={styles.clearButton}
                                onClick={clearRange}
                            >
                                入力をクリア
                            </button>
                            <button
                                type="button"
                                className={styles.confirmButton}
                                onClick={applyDraftAndClose}
                            >
                                決定
                            </button>
                        </div>
                    </div>
                </Overlay>
            )}
        </div>
    );
};

export default DateRangeFormField;
